import { CARE_TIME_SHIFT_REGEX } from '../shared/careDateTimePatterns';

const MULTI_EVENT_TRANSITION_REGEX = /\b(?:then|after|after school|after that|later|next|meanwhile)\b/i;
const CONTEXT_CUE_REGEX = /\b(?:just for context|for context|already|because|since|from a rough night|rough night|rough morning|context)\b/i;
const UNCLEAR_CUE_REGEX = /\b(?:not sure|so far|how the day will go|kind of|sort of)\b/i;

const splitCandidateSegments = (text = '') => {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/(?:,\s+|\.\s+|\bthen\b\s+|\bafter that\b\s+|\blater\b\s+)/i)
    .map((segment) => segment.trim())
    .filter(Boolean);
};

const getMainEventAndContext = (text = '', category = 'log') => {
  const normalized = String(text || '').trim();
  if (!normalized) {
    return {
      mainEventText: '',
      contextText: '',
    };
  }

  const explicitContextMatch = normalized.match(/^(.*?),(?:\s*)(?:and\s+)?(just for context.*)$/i);
  if (explicitContextMatch) {
    return {
      mainEventText: explicitContextMatch[1].trim(),
      contextText: explicitContextMatch[2].trim(),
    };
  }

  const clauseParts = normalized.split(/\b(?:because|since)\b/i).map((part) => part.trim()).filter(Boolean);
  if (clauseParts.length > 1 && category !== 'log') {
    return {
      mainEventText: clauseParts[0],
      contextText: clauseParts.slice(1).join(' because ').trim(),
    };
  }

  return {
    mainEventText: normalized,
    contextText: '',
  };
};

export const buildQuickCaptureStructureDecision = ({
  originalText = '',
  normalizedText = '',
  categoryDecision = {},
  routingDecision = {},
  transcriptNormalization = {},
} = {}) => {
  const sourceText = String(normalizedText || originalText || '').trim();
  const candidateSegments = splitCandidateSegments(sourceText);
  const reasons = [];
  const category = String(categoryDecision?.finalCategory || categoryDecision?.originalCategory || 'log').trim().toLowerCase();
  const routingMode = String(routingDecision?.mode || '').trim().toLowerCase();
  const timeShiftCount = Array.from(sourceText.matchAll(CARE_TIME_SHIFT_REGEX)).length;
  const hasMultiEventSignals = Boolean(
    transcriptNormalization?.hasMultipleEvents
    || MULTI_EVENT_TRANSITION_REGEX.test(sourceText)
    || timeShiftCount >= 2
    || candidateSegments.length >= 3
  );
  const hasContextSignals = CONTEXT_CUE_REGEX.test(sourceText);
  const hasUnclearSignals = UNCLEAR_CUE_REGEX.test(sourceText)
    || transcriptNormalization?.reasons?.includes('rambly_narrative')
    || routingMode === 'ai_smart_capture'
    || routingDecision?.reason === 'low_confidence';

  if (hasMultiEventSignals) {
    reasons.push('multiple_event_signals');
  }
  if (hasContextSignals) {
    reasons.push('context_signals');
  }
  if (hasUnclearSignals) {
    reasons.push('unclear_signals');
  }
  if (timeShiftCount >= 2) {
    reasons.push('time_shifts');
  }

  let structureType = 'single_event';
  let suggestedAction = 'continue_existing_flow';
  let confidence = 'high';

  const explicitContextLead = /^(.*?),(?:\s*)(?:and\s+)?just for context\b/i.test(sourceText);

  if (hasContextSignals && explicitContextLead && category && category !== 'log') {
    structureType = 'single_event_with_context';
    suggestedAction = 'add_context';
    confidence = 'medium';
  } else if (hasMultiEventSignals && candidateSegments.length >= 2) {
    structureType = 'multiple_events';
    suggestedAction = 'suggest_split';
    confidence = 'high';
  } else if (hasContextSignals && category && category !== 'log') {
    structureType = 'single_event_with_context';
    suggestedAction = 'add_context';
    confidence = 'medium';
  } else if (hasUnclearSignals && (category === 'log' || transcriptNormalization?.isLikelyDictated)) {
    structureType = 'unclear';
    suggestedAction = 'force_review';
    confidence = 'low';
  }

  if (structureType === 'single_event' && candidateSegments.length <= 1 && category !== 'log') {
    reasons.push('single_clear_event');
  }

  const { mainEventText, contextText } = getMainEventAndContext(sourceText, category);

  return {
    structureType,
    mainEventText,
    contextText,
    candidateSegments,
    suggestedAction,
    confidence,
    reasons,
  };
};

export default buildQuickCaptureStructureDecision;
